import React, { useEffect, useState } from "react";
import { Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import UploadJsonForm from "./UploadJsonForm";
import GitSuiteForm from "./GitSuiteForm";
import { TabPanel, a11yProps } from "../TabPanel";
import WizardStorageSuite from "./Artifact/WizardStorageSuite";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import { useListStorages } from "../../../../hooks/useStorage";
import HelpTip from "../../../../components/HelpTip";
import { pageHelp } from "../../../../components/help/pageHelp";

const creationModes = [
  {
    label: "Link Repository",
    description: "Use Git repositories for framework and test cases.",
    icon: <AccountTreeIcon fontSize="small" />,
  },
  {
    label: "Remote",
    description: "Create a suite from remote JSON and uploaded case files.",
    icon: <SettingsRemoteIcon fontSize="small" />,
  },
  {
    label: "Artifact Storage",
    description: "Pick framework, cases, and resources from cloud storage.",
    icon: <CloudQueueIcon fontSize="small" />,
  },
];

const SuitCreate = () => {
  const [value, setValue] = useState(0);
  const location = useLocation();

  // Artifact Storage mode needs a configured cloud provider. When /storage is
  // unavailable that tab is disabled (consistent with the Artifacts page).
  const { data: buckets = [], isError: storageError, isLoading: storageLoading } = useListStorages();
  const cloudUnavailable = !storageLoading && (storageError || buckets.length === 0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 2 && cloudUnavailable) return;
    setValue(newValue);
  };

  useEffect(() => {
    if (location.pathname.includes("upload")) setValue(1);
    else if (location.pathname.includes("artifact")) setValue(cloudUnavailable ? 0 : 2);
    else if (location.pathname.includes("link")) setValue(0);
  }, [location.pathname, cloudUnavailable]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1120, width: "100%" }}>
        <Typography
          component="h1"
          sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, lineHeight: 1.2 }}
        >
          Create suite
          <HelpTip size={18} title={pageHelp.createSuite.help} />
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 14, mt: 0.75 }}>
          Choose how DeviceSure should collect the framework, cases, and resources.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 1,
          maxWidth: 1120,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Suite creation tabs"
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            minHeight: { xs: 72, sm: 76 },
            "& .MuiTabs-flexContainer": {
              alignItems: "stretch",
            },
            "& .MuiTab-root": {
              alignItems: "flex-start",
              flex: "1 1 0",
              maxWidth: "none",
              minHeight: { xs: 72, sm: 76 },
              minWidth: 0,
              px: { xs: 1, sm: 2.5 },
              py: 1.5,
              textAlign: "left",
              textTransform: "none",
            },
          }}
        >
          {creationModes.map((mode, index) => (
            <Tab
              key={mode.label}
              {...a11yProps(index)}
              disabled={index === 2 && cloudUnavailable}
              label={
                <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      bgcolor: value === index ? "primary.main" : "background.level1",
                      borderRadius: 1,
                      color: value === index ? "primary.contrastText" : "text.secondary",
                      display: "flex",
                      flexShrink: 0,
                      height: { xs: 28, sm: 32 },
                      justifyContent: "center",
                      width: { xs: 28, sm: 32 },
                    }}
                  >
                    {mode.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 13, sm: 14 },
                        fontWeight: 800,
                        lineHeight: 1.2,
                        whiteSpace: "normal",
                      }}
                    >
                      {mode.label}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        fontSize: 12,
                        lineHeight: 1.35,
                        mt: 0.25,
                        whiteSpace: "normal",
                      }}
                    >
                      {mode.description}
                    </Typography>
                  </Box>
                </Stack>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          <TabPanel value={value} index={0}>
            <GitSuiteForm />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <UploadJsonForm />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <WizardStorageSuite />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default SuitCreate;
